import React, { useState, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Table, Tag, Input, DatePicker, Tooltip, Space, message, Badge, Collapse, Alert } from 'antd';
import { ReloadOutlined, SearchOutlined, ClearOutlined, CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, WarningOutlined, InfoCircleOutlined, SyncOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDepositMonitor } from '../hooks/useTransactionManagement';
import { useCoins } from '../hooks/useCoinManagement';
import { useNetworks } from '../hooks/useNetworkManagement';
import dayjs from 'dayjs';
import SearchableSelect from '../components/common/SearchableSelect';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const DepositMonitorPage: React.FC = () => {
  const { t } = useTranslation();

  // State for filters
  const [searchText, setSearchText] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [blockchainFilter, setBlockchainFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [callbackStatusFilter, setCallbackStatusFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<dayjs.Dayjs | null>(null);
  const [dateToFilter, setDateToFilter] = useState<dayjs.Dayjs | null>(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  // State for sorting
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Calculate skip value for pagination
  const skip = (currentPage - 1) * pageSize;

  // Build filters object
  const buildFilters = () => {
    const filters: any = {};

    if (searchText) {
      filters.txid = searchText;
    }

    if (userIdFilter) {
      filters.user_id = parseInt(userIdFilter, 10);
    }

    if (blockchainFilter && blockchainFilter !== 'all') {
      filters.blockchain = blockchainFilter;
    }

    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }

    if (callbackStatusFilter && callbackStatusFilter !== 'all') {
      filters.callback_status = callbackStatusFilter;
    }

    if (dateFromFilter) {
      filters.from_date = dateFromFilter.toISOString();
    }

    if (dateToFilter) {
      filters.to_date = dateToFilter.toISOString();
    }

    return filters;
  };

  // Fetch deposit monitor data
  const {
    data: monitorData,
    isLoading,
    error,
    refetch
  } = useDepositMonitor(
    buildFilters(),
    pageSize,
    skip,
    { field: sortField, order: sortOrder }
  );

  // Fetch coins and networks for filters
  const { data: coinsData, isLoading: isCoinsLoading } = useCoins();
  const { data: networksData, isLoading: isNetworksLoading } = useNetworks();

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      console.error('Error fetching deposit monitor data:', error);
      message.error('Failed to load deposit monitor data. Please try again.');
    }
  }, [error]);

  // Function to handle refreshing data
  const handleRefresh = () => {
    refetch();
  };

  // Function to handle clearing filters
  const handleClearFilters = () => {
    setSearchText('');
    setUserIdFilter('');
    setBlockchainFilter('all');
    setStatusFilter('all');
    setCallbackStatusFilter('all');
    setDateFromFilter(null);
    setDateToFilter(null);
    setCurrentPage(1);
  };

  // Function to handle pagination change
  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    }
  };

  // Get unique blockchains from networks data
  // Note: The API uses 'blockchain' field which contains values like 'ETHEREUM_SEPOLIA', 'BSC_TESTNET', etc.
  // These values match the 'full_name' field in the network object
  const blockchainOptions = useMemo(() => {
    const networks = Array.isArray(networksData) ? networksData : networksData?.data || [];

    // Create a map of blockchain values (full_name) to network titles for better display
    const blockchainMap = new Map();
    networks.forEach((network: any) => {
      // The blockchain field in deposits matches the network's full_name field
      const blockchainValue = network.full_name;
      const displayLabel = network.title;

      if (blockchainValue && displayLabel && !blockchainMap.has(blockchainValue)) {
        blockchainMap.set(blockchainValue, displayLabel);
      }
    });

    return [
      { value: 'all', label: 'All Blockchains' },
      ...Array.from(blockchainMap.entries()).map(([value, label]) => ({
        value,
        label
      }))
    ];
  }, [networksData]);

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'completed', label: 'Completed' },
    { value: 'processing', label: 'Processing' },
    { value: 'failed', label: 'Failed' },
    { value: 'callback_failed', label: 'Callback Failed' },
    { value: 'pending', label: 'Pending' }
  ];

  // Callback status options
  const callbackStatusOptions = [
    { value: 'all', label: 'All Callback Status' },
    { value: 'success', label: 'Success' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'error', label: 'Error' },
    { value: 'skipped', label: 'Skipped' }
  ];

  // Render stage status badge
  const renderStageStatus = (stage: any) => {
    if (!stage) return <Tag color="default">Not Started</Tag>;

    const { status, timestamp, error: stageError } = stage;

    let color = 'default';
    let icon = null;
    let displayText = 'Unknown';

    if (status === 'success') {
      color = 'green';
      icon = <CheckCircleOutlined />;
      displayText = 'Success';
    } else if (status === 'error') {
      color = 'red';
      icon = <CloseCircleOutlined />;
      displayText = 'Error';
    } else if (status === 'in_progress') {
      color = 'blue';
      icon = <SyncOutlined spin />;
      displayText = 'In Progress';
    } else if (status === 'pending') {
      color = 'orange';
      icon = <ClockCircleOutlined />;
      displayText = 'Pending';
    } else if (status === 'skipped') {
      color = 'default';
      displayText = 'Skipped';
    }

    const tooltipContent = stageError || (timestamp ? new Date(timestamp).toLocaleString() : '');

    return (
      <Tooltip title={tooltipContent}>
        <Tag color={color} icon={icon}>
          {displayText}
        </Tag>
      </Tooltip>
    );
  };

  // Render processing stages
  const renderProcessingStages = (record: any) => {
    const { stages } = record;

    if (!stages) {
      return <Text type="secondary">No stage data</Text>;
    }

    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <div><Text strong>Webhook:</Text> {renderStageStatus(stages.webhook_received)}</div>
        <div><Text strong>Processing:</Text> {renderStageStatus(stages.initial_processing)}</div>
        <div><Text strong>Top-Up:</Text> {renderStageStatus(stages.top_up)}</div>
        <div><Text strong>Transfer:</Text> {renderStageStatus(stages.transfer)}</div>
        <div><Text strong>Callback:</Text> {renderStageStatus(stages.callback)}</div>
      </Space>
    );
  };

  // Render stage details in expandable row
  const renderStageDetails = (record: any) => {
    const { stages, overall_error } = record;

    if (!stages) {
      return <Text type="secondary">No detailed stage information available</Text>;
    }

    // Collect all errors from stages
    const errors: Array<{ stage: string; error: string }> = [];

    if (stages.webhook_received?.error) {
      errors.push({ stage: 'Webhook Received', error: stages.webhook_received.error });
    }
    if (stages.initial_processing?.error) {
      errors.push({ stage: 'Initial Processing', error: stages.initial_processing.error });
    }
    if (stages.top_up?.error) {
      errors.push({ stage: 'Gas Top-Up', error: stages.top_up.error });
    }
    if (stages.transfer?.error) {
      errors.push({ stage: 'Transfer to Warm Wallet', error: stages.transfer.error });
    }
    if (stages.callback?.error) {
      errors.push({ stage: 'Callback to BitAPI', error: stages.callback.error });
    }
    if (overall_error) {
      errors.push({ stage: 'Overall', error: overall_error });
    }

    const renderDetailedStage = (stageName: string, stage: any) => {
      if (!stage) return null;

      const hasError = stage.status === 'error' || stage.error;
      const isSkipped = stage.status === 'skipped';

      return (
        <Card
          size="small"
          title={
            <Space>
              {stageName}
              {hasError && <Tag color="red" icon={<ExclamationCircleOutlined />}>ERROR</Tag>}
              {isSkipped && <Tag color="default">SKIPPED</Tag>}
            </Space>
          }
          style={{
            marginBottom: 8,
            borderColor: hasError ? '#ff4d4f' : undefined,
            borderWidth: hasError ? 2 : 1
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div><Text strong>Status:</Text> {renderStageStatus(stage)}</div>
            {stage.timestamp && (
              <div><Text strong>Timestamp:</Text> {new Date(stage.timestamp).toLocaleString()}</div>
            )}
            {stage.completed_at && (
              <div><Text strong>Completed:</Text> {new Date(stage.completed_at).toLocaleString()}</div>
            )}
            {stage.error && (
              <Alert
                message="Error Details"
                description={stage.error}
                type="error"
                showIcon
                style={{ marginTop: 8 }}
              />
            )}

            {/* Special rendering for Callback stage */}
            {stageName === 'Callback to BitAPI' && stage.details && (
              <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                {stage.details.callback_url && (
                  <div>
                    <Text strong>Callback URL:</Text>
                    <div style={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      backgroundColor: '#f0f0f0',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      marginTop: 4,
                      wordBreak: 'break-all'
                    }}>
                      {stage.details.callback_url}
                    </div>
                  </div>
                )}

                {stage.details.callback_request && (
                  <div>
                    <Text strong>Request Payload:</Text>
                    <pre style={{
                      fontSize: '11px',
                      marginTop: 4,
                      backgroundColor: '#e6f7ff',
                      padding: '8px',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      maxWidth: '100%',
                      overflow: 'auto',
                      border: '1px solid #91d5ff',
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {JSON.stringify(stage.details.callback_request, null, 2)}
                    </pre>
                  </div>
                )}

                {stage.details.callback_response && (
                  <div>
                    <Text strong>Response:</Text>
                    <pre style={{
                      fontSize: '11px',
                      marginTop: 4,
                      backgroundColor: stage.status === 'success' ? '#f6ffed' : '#fff2e8',
                      padding: '8px',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      maxWidth: '100%',
                      overflow: 'auto',
                      border: stage.status === 'success' ? '1px solid #b7eb8f' : '1px solid #ffd591',
                      wordBreak: 'break-all',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {JSON.stringify(stage.details.callback_response, null, 2)}
                    </pre>
                  </div>
                )}

                {stage.details.callback_error && (
                  <Alert
                    message="Callback Error"
                    description={stage.details.callback_error}
                    type="error"
                    showIcon
                  />
                )}

                {/* Show message for old records without callback details */}
                {!stage.details.callback_url && !stage.details.callback_request &&
                  !stage.details.callback_response && !stage.details.callback_error &&
                  stage.status !== 'pending' && (
                    <Alert
                      message="Legacy Record"
                      description="This is an older deposit record. Detailed callback information (URL, request, response) is only available for new deposits processed after the monitoring update."
                      type="info"
                      showIcon
                      style={{ fontSize: '11px' }}
                    />
                  )}
              </Space>
            )}

            {/* Regular details for other stages */}
            {stageName !== 'Callback to BitAPI' && stage.details && Object.keys(stage.details).length > 0 && (
              <div>
                <Text strong>Details:</Text>
                <pre style={{
                  fontSize: '11px',
                  marginTop: 4,
                  backgroundColor: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  maxWidth: '100%',
                  overflow: 'auto',
                  wordBreak: 'break-all',
                  whiteSpace: 'pre-wrap'
                }}>
                  {JSON.stringify(stage.details, null, 2)}
                </pre>
              </div>
            )}
          </Space>
        </Card>
      );
    };

    return (
      <div style={{ padding: '16px', maxWidth: '100%', overflow: 'hidden' }}>
        {/* Transaction Details Section */}
        <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <div>
              <Text strong style={{ fontSize: '12px' }}>Hot Wallet: </Text>
              <Tooltip title="Click to copy address">
                <Text
                  copyable
                  code
                  style={{ fontSize: '11px', cursor: 'pointer' }}
                >
                  {record.wallet?.public_address || record.address || 'N/A'}
                </Text>
              </Tooltip>
            </div>
            {record.transfer_txid && (
              <div>
                <Text strong style={{ fontSize: '12px' }}>Transfer TX: </Text>
                <Tooltip title="Click to copy transaction ID">
                  <Text
                    copyable
                    code
                    style={{ fontSize: '11px', cursor: 'pointer' }}
                  >
                    {record.transfer_txid}
                  </Text>
                </Tooltip>
              </div>
            )}
            {record.topup_txid && (
              <div>
                <Text strong style={{ fontSize: '12px' }}>Top-up TX: </Text>
                <Tooltip title="Click to copy transaction ID">
                  <Text
                    copyable
                    code
                    style={{ fontSize: '11px', cursor: 'pointer' }}
                  >
                    {record.topup_txid}
                  </Text>
                </Tooltip>
              </div>
            )}
          </Space>
        </Card>

        {/* Error Summary Section */}
        {errors.length > 0 && (
          <Alert
            message={`${errors.length} Error${errors.length > 1 ? 's' : ''} Detected`}
            description={
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {errors.map((err, index) => (
                  <div key={index}>
                    <Text strong type="danger">{err.stage}:</Text> <Text>{err.error}</Text>
                  </div>
                ))}
              </Space>
            }
            type="error"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Stage Details */}
        <Row gutter={16}>
          <Col span={12}>
            {renderDetailedStage('Webhook Received', stages.webhook_received)}
            {renderDetailedStage('Initial Processing', stages.initial_processing)}
            {renderDetailedStage('Gas Top-Up', stages.top_up)}
          </Col>
          <Col span={12}>
            {renderDetailedStage('Transfer to Warm Wallet', stages.transfer)}
            {renderDetailedStage('Callback to BitAPI', stages.callback)}
          </Col>
        </Row>

        {/* Sweep Information */}
        {record.sweep_queued && (
          <Card
            size="small"
            title={
              <Space>
                Sweep Information
                {record.sweep_status && (
                  <Tag color={
                    record.sweep_status === 'Completed' ? 'green' :
                      record.sweep_status === 'Failed' ? 'red' :
                        record.sweep_status === 'Processing' ? 'orange' :
                          record.sweep_status === 'Queued' ? 'blue' : 'default'
                  }>
                    {record.sweep_status}
                  </Tag>
                )}
              </Space>
            }
            style={{ marginTop: 16 }}
          >
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <div><Text strong>Sweep Queued:</Text> {record.sweep_queued ? 'Yes' : 'No'}</div>
              {record.sweep_queued_at && (
                <div><Text strong>Queued At:</Text> {new Date(record.sweep_queued_at).toLocaleString()}</div>
              )}
              {record.sweep_job_id && (
                <div><Text strong>Job ID:</Text> <Text code>{record.sweep_job_id}</Text></div>
              )}
              {record.sweep_txid && (
                <div>
                  <Text strong>Sweep TXID:</Text>
                  <Tooltip title="Click to copy">
                    <Text
                      code
                      copyable
                      style={{ marginLeft: 8, cursor: 'pointer' }}
                    >
                      {record.sweep_txid}
                    </Text>
                  </Tooltip>
                </div>
              )}
              {record.sweep_completed_at && (
                <div><Text strong>Completed At:</Text> {new Date(record.sweep_completed_at).toLocaleString()}</div>
              )}
              {record.sweep_error && (
                <Alert
                  message="Sweep Error"
                  description={record.sweep_error}
                  type="error"
                  showIcon
                  style={{ marginTop: 8 }}
                />
              )}
              {record.sweep_result && (
                <div>
                  <Text strong>Sweep Result:</Text>
                  {record.sweep_status === 'NotNeeded' ? (
                    // Format balance info for "No sweep needed" case
                    <div style={{ marginTop: 8 }}>
                      <Row gutter={[16, 8]}>
                        <Col span={12}>
                          <Text type="secondary">Balance:</Text>
                          <div><Text strong>{record.sweep_result.currentBalance || 'N/A'} {record.sweep_result.coinSymbol || ''}</Text></div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">Balance (USD):</Text>
                          <div><Text strong>${record.sweep_result.currentBalanceUSD?.toFixed(2) || 'N/A'}</Text></div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">Max Threshold (USD):</Text>
                          <div><Text strong>${record.sweep_result.maxBalanceUSD?.toFixed(2) || 'N/A'}</Text></div>
                        </Col>
                        {record.sweep_result.coinPriceUSD && (
                          <Col span={12}>
                            <Text type="secondary">Coin Price (USD):</Text>
                            <div><Text strong>${record.sweep_result.coinPriceUSD?.toFixed(2)}</Text></div>
                          </Col>
                        )}
                      </Row>
                      <Alert
                        message="Balance is below the maximum threshold, no sweep needed"
                        type="info"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  ) : record.sweep_status === 'Completed' ? (
                    // Format sweep completion info
                    <div style={{ marginTop: 8 }}>
                      <Row gutter={[16, 8]}>
                        <Col span={12}>
                          <Text type="secondary">Swept Amount:</Text>
                          <div><Text strong>{record.sweep_result.sweepAmount || 'N/A'} {record.sweep_result.coinSymbol || ''}</Text></div>
                        </Col>
                        <Col span={12}>
                          <Text type="secondary">New Balance:</Text>
                          <div><Text strong>{record.sweep_result.newBalance || 'N/A'} {record.sweep_result.coinSymbol || ''}</Text></div>
                        </Col>
                        {record.sweep_result.warmWalletId && (
                          <Col span={24}>
                            <Text type="secondary">Warm Wallet ID:</Text>
                            <div><Text strong code copyable>{record.sweep_result.warmWalletId}</Text></div>
                          </Col>
                        )}
                      </Row>
                      <Alert
                        message="Funds successfully swept to warm wallet"
                        type="success"
                        showIcon
                        style={{ marginTop: 8 }}
                      />
                    </div>
                  ) : (
                    // Show raw JSON for other statuses
                    <pre style={{
                      fontSize: '11px',
                      marginTop: 4,
                      backgroundColor: '#f0f0f0',
                      padding: '8px',
                      borderRadius: '4px',
                      maxHeight: '150px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(record.sweep_result, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </Space>
          </Card>
        )}
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'txid',
      key: 'txid',
      width: 150,
      render: (text: string) => (
        <Tooltip title="Click to copy transaction ID">
          <span
            style={{ fontFamily: 'monospace', cursor: 'pointer', fontSize: '12px' }}
            onClick={() => {
              navigator.clipboard.writeText(text);
              message.success('Transaction ID copied to clipboard');
            }}
          >
            {text && text.length > 15 ? `${text.substring(0, 8)}...${text.substring(text.length - 6)}` : text}
            <CopyOutlined style={{ marginLeft: 4, fontSize: '10px', color: '#1890ff' }} />
          </span>
        </Tooltip>
      )
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
      sorter: true
    },
    {
      title: 'Blockchain',
      dataIndex: 'blockchain',
      key: 'blockchain',
      width: 120,
      render: (text: string) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      sorter: true,
      render: (amount: string, record: any) => {
        const coinSymbol = record.coin?.symbol || record.token_symbol || '';
        return `${amount} ${coinSymbol}`;
      }
    },
    {
      title: 'Overall Status',
      dataIndex: 'overall_status',
      key: 'overall_status',
      width: 150,
      render: (status: string, record: any) => {
        let color = 'default';
        let icon = null;

        if (status === 'completed') {
          color = 'green';
          icon = <CheckCircleOutlined />;
        } else if (status === 'failed' || status === 'callback_failed') {
          color = 'red';
          icon = <CloseCircleOutlined />;
        } else if (status === 'processing') {
          color = 'blue';
          icon = <SyncOutlined spin />;
        } else if (status === 'pending') {
          color = 'orange';
          icon = <ClockCircleOutlined />;
        }

        // Check if there are any errors in stages
        const hasErrors = record.overall_error ||
          record.stages?.webhook_received?.error ||
          record.stages?.initial_processing?.error ||
          record.stages?.top_up?.error ||
          record.stages?.transfer?.error ||
          record.stages?.callback?.error;

        return (
          <Space direction="vertical" size={2}>
            <Tag color={color} icon={icon}>
              {status?.toUpperCase()}
            </Tag>
            {hasErrors && (
              <Tooltip title="Click to expand and view error details">
                <Tag color="red" icon={<ExclamationCircleOutlined />} style={{ cursor: 'pointer' }}>
                  HAS ERRORS
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Processing Stages',
      key: 'stages',
      width: 200,
      render: (_: any, record: any) => renderProcessingStages(record)
    },
    {
      title: 'Sweep Status',
      key: 'sweep_status',
      width: 120,
      render: (_: any, record: any) => {
        const sweepStatus = record.sweep_status;

        if (!sweepStatus) {
          return <Tag color="default">N/A</Tag>;
        }

        let color = 'default';
        let icon = null;

        switch (sweepStatus) {
          case 'Queued':
            color = 'blue';
            icon = <ClockCircleOutlined />;
            break;
          case 'Processing':
            color = 'orange';
            icon = <SyncOutlined spin />;
            break;
          case 'Completed':
            color = 'green';
            icon = <CheckCircleOutlined />;
            break;
          case 'Failed':
            color = 'red';
            icon = <CloseCircleOutlined />;
            break;
          case 'NotNeeded':
            color = 'default';
            icon = <InfoCircleOutlined />;
            break;
        }

        // Display label
        const displayLabel = sweepStatus === 'NotNeeded' ? 'No sweep needed' : sweepStatus;

        // Format tooltip content
        let tooltipContent: React.ReactNode = 'No details';
        if (record.sweep_error) {
          tooltipContent = record.sweep_error;
        } else if (record.sweep_result) {
          if (sweepStatus === 'NotNeeded') {
            // Format balance info for "No sweep needed" case
            const result = record.sweep_result;
            tooltipContent = (
              <div>
                <div><strong>Balance:</strong> {result.currentBalance || 'N/A'} {result.coinSymbol || ''}</div>
                <div><strong>Balance USD:</strong> ${result.currentBalanceUSD?.toFixed(2) || 'N/A'}</div>
                <div><strong>Max Threshold:</strong> ${result.maxBalanceUSD?.toFixed(2) || 'N/A'}</div>
                {result.coinPriceUSD && <div><strong>Price:</strong> ${result.coinPriceUSD?.toFixed(2)}</div>}
              </div>
            );
          } else if (sweepStatus === 'Completed') {
            // Format sweep completion info
            const result = record.sweep_result;
            tooltipContent = (
              <div>
                <div><strong>Swept Amount:</strong> {result.sweepAmount || 'N/A'} {result.coinSymbol || ''}</div>
                <div><strong>New Balance:</strong> {result.newBalance || 'N/A'} {result.coinSymbol || ''}</div>
                {result.warmWalletId && <div><strong>Warm Wallet:</strong> {result.warmWalletId}</div>}
              </div>
            );
          } else {
            tooltipContent = JSON.stringify(record.sweep_result);
          }
        }

        return (
          <span key={`sweep-status-${record._id}`}>
            <Tooltip title={tooltipContent} fresh>
              <Tag color={color} icon={icon}>
                {displayLabel}
              </Tag>
            </Tooltip>
          </span>
        );
      }
    },
    {
      title: 'Created / Duration',
      key: 'created_duration',
      width: 200,
      sorter: true,
      render: (_: any, record: any) => {
        const createdAt = record.created_at ? new Date(record.created_at) : null;
        const completedAt = record.completed_at ? new Date(record.completed_at) : null;

        // Calculate total duration if both dates exist
        let totalDuration = null;
        if (createdAt && completedAt) {
          const durationMs = completedAt.getTime() - createdAt.getTime();
          const seconds = Math.floor(durationMs / 1000);
          const minutes = Math.floor(seconds / 60);
          const hours = Math.floor(minutes / 60);

          if (hours > 0) {
            totalDuration = `${hours}h ${minutes % 60}m`;
          } else if (minutes > 0) {
            totalDuration = `${minutes}m ${seconds % 60}s`;
          } else {
            totalDuration = `${seconds}s`;
          }
        }

        // Calculate callback duration
        let callbackDuration = null;
        if (record.callback_duration_ms) {
          const seconds = Math.floor(record.callback_duration_ms / 1000);
          const minutes = Math.floor(seconds / 60);

          if (minutes > 0) {
            callbackDuration = `${minutes}m ${seconds % 60}s`;
          } else {
            callbackDuration = `${seconds}s`;
          }
        }

        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: '12px' }}>
              {createdAt ? createdAt.toLocaleString() : '-'}
            </Text>
            {callbackDuration && (
              <Tag color="green" style={{ fontSize: '11px', marginTop: 2 }}>
                📞 {callbackDuration}
              </Tag>
            )}
            {totalDuration && (
              <Tag color="blue" style={{ fontSize: '11px', marginTop: 2 }}>
                ⏱ {totalDuration}
              </Tag>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} align="middle">
        <Col span={12}>
          <Title level={2}>Deposit Monitor</Title>
          <Text type="secondary">
            Monitor deposit transactions and their processing stages
          </Text>
        </Col>
        <Col span={12} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isLoading}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        {/* Search and Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <div className="filter-label">Search</div>
            <Input
              placeholder="Search by transaction ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              onPressEnter={() => setCurrentPage(1)}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">User ID</div>
            <Input
              placeholder="User ID"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              allowClear
              type="number"
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Blockchain</div>
            <SearchableSelect
              placeholder="Select Blockchain"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={blockchainFilter}
              onChange={(value) => {
                setBlockchainFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={blockchainOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Status</div>
            <SearchableSelect
              placeholder="Select Status"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={statusOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Callback</div>
            <SearchableSelect
              placeholder="Callback Status"
              searchPlaceholder="Type to search..."
              style={{ width: '100%' }}
              value={callbackStatusFilter}
              onChange={(value) => {
                setCallbackStatusFilter(value);
                setCurrentPage(1);
              }}
              allowClear
              options={callbackStatusOptions}
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Date From</div>
            <DatePicker
              placeholder="Select Date From"
              style={{ width: '100%' }}
              value={dateFromFilter}
              onChange={(date) => {
                setDateFromFilter(date);
                setCurrentPage(1);
              }}
              allowClear
            />
          </Col>
          <Col span={3}>
            <div className="filter-label">Date To</div>
            <DatePicker
              placeholder="Select Date To"
              style={{ width: '100%' }}
              value={dateToFilter}
              onChange={(date) => {
                setDateToFilter(date);
                setCurrentPage(1);
              }}
              allowClear
            />
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
              style={{ marginRight: 8 }}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>

        {/* Summary Stats */}
        {monitorData?.success && (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <Text type="secondary">Total Deposits</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {monitorData.pagination?.total || 0}
                </Title>
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Text type="secondary">Current Page</Text>
                <Title level={4} style={{ margin: 0 }}>
                  {monitorData.data?.length || 0}
                </Title>
              </Card>
            </Col>
          </Row>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={monitorData?.data || []}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: monitorData?.pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, total)} of ${total} deposits`
          }}
          onChange={handleTableChange}
          rowKey="_id"
          loading={isLoading}
          scroll={{ x: 1600 }}
          size="small"
          expandable={{
            expandedRowRender: renderStageDetails,
            rowExpandable: (record) => !!record.stages
          }}
        />
      </Card>
    </div>
  );
};

export default DepositMonitorPage;

